import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, TrendingUp, TrendingDown, Minus } from "lucide-react";

const FALLBACK_BUCKET_LABELS = {
  activities: "Activities Today",
  mood: "Mood / Demeanor",
  meals: "Meals Consumed",
  attendance_checks: "Attendance Checklist",
  pcsp_goals: "Client Goals Worked On",
};

const PALETTE = ["#2563eb", "#a855f7", "#f59e0b", "#10b981", "#ec4899", "#0ea5e9", "#dc2626", "#7c3aed"];

const colorFor = (bucket, idx) => PALETTE[idx % PALETTE.length];

/** Lightweight sparkline / bar combined inline (no external chart lib).
 *  Series: array of { date, [bucket]: number }
 */
function MiniLineChart({ series, bucket, color }) {
  const w = 520, h = 110, pad = 8;
  const xs = series.map((_, i) => pad + (i * (w - pad * 2)) / Math.max(1, series.length - 1));
  const max = Math.max(1, ...series.map((p) => p[bucket] || 0));
  const ys = series.map((p) => h - pad - ((p[bucket] || 0) * (h - pad * 2)) / max);
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const area = `${path} L${xs[xs.length - 1].toFixed(1)},${h - pad} L${xs[0].toFixed(1)},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} className="block" aria-hidden="true">
      <path d={area} fill={color} fillOpacity="0.12" />
      <path d={path} stroke={color} strokeWidth="2" fill="none" />
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#E2E8F0" strokeWidth="1" />
    </svg>
  );
}

function TrendIcon({ totals30, totals60 }) {
  const recent = totals30;
  const prior = Math.max(0, (totals60 || 0) - (totals30 || 0));
  if (recent > prior * 1.1) return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (recent < prior * 0.9) return <TrendingDown className="w-4 h-4 text-red-600" />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
}

export default function AdminAnalytics() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
      if (data[0]) setClientId(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!clientId) return;
    api.get(`/outcome-analytics?client_id=${clientId}`).then(({ data }) => setData(data));
  }, [clientId]);

  const active = clients.find((c) => c.id === clientId);
  const buckets = data?.buckets || [];
  const bucketTitles = data?.bucket_titles || {};
  const bucketCapacities = data?.bucket_capacities || {};
  const labelFor = (b) => bucketTitles[b] || FALLBACK_BUCKET_LABELS[b] || b;
  const pctFor = (b) => {
    const capacity = bucketCapacities[b] || 1;
    const opportunities = (data?.note_days_30d || 0) * capacity;
    if (opportunities <= 0) return 0;
    return Math.min(100, Math.round(((data.totals_30d[b] || 0) / opportunities) * 100));
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-label">Oversight &amp; Reporting</p>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="w-7 h-7 text-primary" /> Client Outcome Analytics
          </h1>
          <p className="text-sm text-muted-foreground">
            Rolling 30 / 60 / 90-day frequency trends across measurable daily-note objectives.
          </p>
        </div>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-72" data-testid="analytics-client-select"><SelectValue /></SelectTrigger>
          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!data && <p className="text-sm text-muted-foreground">Loading analytics…</p>}
      {data && (
        <>
          <Card className="p-5">
            <p className="text-label mb-2">Monthly Summary — {active?.name}</p>
            <p className="text-sm text-muted-foreground mb-4">
              {data.note_days_30d} of {data.month_days} days had a Daily Note submitted in the last 30 days.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-summary-grid">
              {buckets.map((b, i) => {
                const capacity = bucketCapacities[b] || 0;
                const opportunities = (data.note_days_30d || 0) * capacity;
                return (
                  <div key={b} className="rounded-md border border-border p-3 bg-secondary/30" data-testid={`summary-${b}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: colorFor(b, i) }}>
                      {labelFor(b)}
                    </p>
                    <p className="font-display text-2xl font-bold mt-1">
                      <span data-testid={`summary-count-${b}`}>{data.totals_30d[b]}</span>
                      <span className="text-sm text-muted-foreground font-normal"> / {opportunities}</span>
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`summary-pct-${b}`}>
                      {pctFor(b)}% of opportunities
                      <span className="text-[10px] block">
                        ({capacity} item{capacity === 1 ? "" : "s"} × {data.note_days_30d} day{data.note_days_30d === 1 ? "" : "s"})
                      </span>
                    </p>
                    <div className="flex items-center gap-1 text-[11px] mt-1 text-muted-foreground">
                      <TrendIcon totals30={data.totals_30d[b]} totals60={data.totals_60d[b]} />
                      <span>vs. prior 30 days</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5 space-y-5" data-testid="analytics-charts">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-label">Frequency Trend — last 90 days</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {buckets.map((b, i) => (
                  <span key={b} className="inline-flex items-center gap-1">
                    <span className="h-2 w-3 rounded" style={{ backgroundColor: colorFor(b, i) }} />
                    {labelFor(b)}
                  </span>
                ))}
              </div>
            </div>
            {buckets.map((b, i) => (
              <div key={b} className="space-y-1" data-testid={`chart-${b}`}>
                <div className="flex items-center justify-between text-xs">
                  <p className="font-semibold" style={{ color: colorFor(b, i) }}>{labelFor(b)}</p>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span>30d: <b className="text-foreground">{data.totals_30d[b]}</b></span>
                    <span>60d: <b className="text-foreground">{data.totals_60d[b]}</b></span>
                    <span>90d: <b className="text-foreground">{data.totals_90d[b]}</b></span>
                  </div>
                </div>
                <MiniLineChart series={data.series} bucket={b} color={colorFor(b, i)} />
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
